import { useEffect, useMemo, useState } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { OffshoreGatepassFormData } from "@features/gatepass/schema/offshoreGatepassFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES, registrationEmirates } from "@services/referenceDataApi";
import { t } from "i18next";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

type VehicleType = { [key: string]: string };

export interface FormContext {
  formData: UseFormReturn<OffshoreGatepassFormData>;
  updateFormData: (key: string, value: unknown) => void;
}

const TRANSLATION_KEY = "gatepass";

const VehicleDetails = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { trigger } = formData;
  const { options: vehicleTypes } = useFormattedReference<VehicleType>(
    REFERENCE_DATA_TYPES.VEHICLE_TYPES,
    "bff-gatepass",
  );
  const vehicleType = formData.watch("vehicleType");
  const hasCamera = formData.watch("hasCamera");
  const cameraSerialNumber = formData.watch("cameraSerialNumber");
  const cameraModel = formData.watch("cameraModel");
  const [showVehicleFields, setShowVehicleFields] = useState(false);

  /**
   * Re-run Zod for camera fields while typing (max length / regex).
   * Intentionally omit `hasCamera` from deps: toggling the checkbox must not trigger validation on empty fields.
   */
  useEffect(() => {
    if (!hasCamera) return;
    void trigger(["cameraSerialNumber"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasCamera omitted; see comment above
  }, [cameraSerialNumber, trigger]);
  useEffect(() => {
    if (!hasCamera) return;
    void trigger(["cameraModel"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasCamera omitted; see comment above
  }, [cameraModel, trigger]);

  const formattedVehicleTypes = useMemo(
    () => [
      { value: "", label: t("gatepass.no-vehicle") },
      ...vehicleTypes.map((vehicleType) => ({
        value: vehicleType[Object.keys(vehicleType)[0]],
        label: vehicleType[Object.keys(vehicleType)[0]],
      })),
    ],
    [vehicleTypes],
  );
  const categoryOptions = useMemo(
    () => [
      { value: "S", label: "Small" },
      { value: "M", label: "Medium" },
      { value: "L", label: "Large" },
    ],
    [],
  );
  useEffect(() => {
    setShowVehicleFields(!!vehicleType && vehicleType !== "");
  }, [vehicleType]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${TRANSLATION_KEY}.vehicle-details`)}>
        <div className="grid col-span-2 grid-cols-4 gap-4">
          <FormItem<OffshoreGatepassFormData, "vehicleType">
            name="vehicleType"
            type="autocomplete"
            label={t(`${TRANSLATION_KEY}.vehicle-type`)}
            options={formattedVehicleTypes}
            testId="offshoreGatepass-vehicleType"
            onFieldChange={updateFormData}
          />
          {showVehicleFields && (
            <>
              <FormItem<OffshoreGatepassFormData, "registrationEmirate">
                required
                name="registrationEmirate"
                type="autocomplete"
                options={registrationEmirates}
                label={t(`${TRANSLATION_KEY}.registration-emirate`)}
                testId="offshoreGatepass-registrationEmirate"
                onFieldChange={updateFormData}
              />
              <FormItem<OffshoreGatepassFormData, "vehicleColour">
                name="vehicleColour"
                type="text"
                label={t(`${TRANSLATION_KEY}.vehicle-colour`)}
                testId="offshoreGatepass-vehicleColour"
                onFieldChange={updateFormData}
              />
              <FormItem<OffshoreGatepassFormData, "category">
                name="category"
                type="autocomplete"
                options={categoryOptions}
                label={t(`${TRANSLATION_KEY}.category`)}
                testId="offshoreGatepass-category"
                onFieldChange={updateFormData}
              />
              <FormItem<OffshoreGatepassFormData, "plateNumber">
                required
                name="plateNumber"
                type="text"
                label={t(`${TRANSLATION_KEY}.vehicle-plate-number`)}
                testId="offshoreGatepass-plateNumber"
                onFieldChange={updateFormData}
              />
            </>
          )}
          <div className="col-span-4">
            <FormItem<OffshoreGatepassFormData, "hasCamera">
              name="hasCamera"
              type="checkbox"
              label={t(`${TRANSLATION_KEY}.i-will-be-carrying-camera`, {
                defaultValue: "I will be carrying a camera",
              })}
              testId="offshoreGatepass-hasCamera"
              onFieldChange={updateFormData}
            />
          </div>
          {hasCamera && (
            <>
              <div className="col-span-2">
                <FormItem<OffshoreGatepassFormData, "cameraSerialNumber">
                  required
                  name="cameraSerialNumber"
                  type="text"
                  label={t(`${TRANSLATION_KEY}.camera-serial-number`, {
                    defaultValue: "Camera serial number",
                  })}
                  testId="offshoreGatepass-cameraSerialNumber"
                  onFieldChange={updateFormData}
                />
              </div>
              <div className="col-span-2">
                <FormItem<OffshoreGatepassFormData, "cameraModel">
                  required
                  name="cameraModel"
                  type="text"
                  label={t(`${TRANSLATION_KEY}.camera-model`, {
                    defaultValue: "Camera model",
                  })}
                  testId="offshoreGatepass-cameraModel"
                  onFieldChange={updateFormData}
                />
              </div>
            </>
          )}
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default VehicleDetails;
